// Curated "further reading" links per simulator topic.
// Pitched at veterinary-nurse standard; biased toward AAHA and Australian
// resources (ANZCVS, VNCA, Melbourne Polytechnic). URLs verified June 2026.
export const FURTHER_READING = {
  scheduling: {
    blurb:
      "Scheduling, secure storage and controlled-drug record-keeping references at veterinary-nurse standard.",
    links: [
      {
        label: "Poisons Standard (SUSMP) — current edition",
        href: "https://www.tga.gov.au/products/regulations-all-products/legislation-and-legislative-instruments/poisons-standard-susmp",
        source: "TGA · Australia",
      },
      {
        label: "Scheduling of medicines and poisons — overview",
        href: "https://www.healthdirect.gov.au/scheduling-of-medicines-and-poisons",
        source: "healthdirect · Australia",
      },
      {
        label: "Storage & recording of Schedule 8 drugs of addiction",
        href: "https://www.health.nsw.gov.au/pharmaceutical/Pages/drugs-of-addiction-sch8.aspx",
        source: "NSW Health",
      },
      {
        label: "Drugs and poisons — clinic obligations & Poisons Code",
        href: "https://www.health.vic.gov.au/drugs-and-poisons/poisons-code",
        source: "Vic Health",
      },
    ],
  },
  anaesthesia: {
    blurb:
      "Deepen your anaesthetic monitoring and patient-safety knowledge with these guideline and continuing-education resources.",
    links: [
      {
        label: "AAHA Anesthesia & Monitoring Guidelines for Dogs & Cats (2020)",
        href: "https://www.aaha.org/resources/2020-aaha-anesthesia-and-monitoring-guidelines-for-dogs-and-cats/",
        source: "AAHA",
      },
      {
        label: "ANZCVS Veterinary Anaesthesia & Analgesia Chapter",
        href: "https://www.anzcvs.org.au/chapters/veterinary+anaesthesia+and+analgesia+chapter",
        source: "ANZCVS · Australia / NZ",
      },
      {
        label: "American College of Veterinary Anesthesia & Analgesia (ACVAA)",
        href: "https://acvaa.org/",
        source: "ACVAA",
      },
      {
        label: "Veterinary Nurses Council of Australia (VNCA)",
        href: "https://vnca.asn.au",
        source: "VNCA · Australia",
      },
    ],
  },

  fluids: {
    blurb:
      "Build on the 2024 AAHA fluid therapy framework with the official guidelines, decision algorithms and downloadable tables.",
    links: [
      {
        label: "AAHA Fluid Therapy Guidelines for Dogs & Cats (2024)",
        href: "https://www.aaha.org/resources/2024-aaha-fluid-therapy-guidelines-for-dogs-and-cats/",
        source: "AAHA",
      },
      {
        label: "AAHA Fluid Therapy Algorithms (intra-op, maintenance, shock)",
        href: "https://www.aaha.org/resources/2024-aaha-fluid-therapy-guidelines-for-dogs-and-cats/fluid-therapy-algorithms/",
        source: "AAHA",
      },
      {
        label: "AAHA Fluid Therapy Tables & Figures (downloadable)",
        href: "https://www.aaha.org/resources/2024-aaha-fluid-therapy-guidelines-for-dogs-and-cats/fluid-therapy-tables-and-figures-for-download/",
        source: "AAHA",
      },
      {
        label: "Veterinary Nurses Council of Australia (VNCA)",
        href: "https://vnca.asn.au",
        source: "VNCA · Australia",
      },
    ],
  },

  ventilation: {
    blurb:
      "Reinforce safe IPPV / manual ventilation technique and airway-pressure monitoring with these clinical resources.",
    links: [
      {
        label: "How to use IPPV during veterinary surgery",
        href: "https://www.theveterinarynurse.com/content/practical/how-to-use-ippv-during-veterinary-surgery",
        source: "The Veterinary Nurse",
      },
      {
        label: "Intermittent Positive Pressure Ventilation — clinical skills guide (PDF)",
        href: "https://www.bristol.ac.uk/media-library/sites/vetscience/documents/clinical-skills/Feline%20Intermittent%20Positive%20Pressure%20Ventilations%20(IPPV).pdf",
        source: "University of Bristol",
      },
      {
        label: "AAHA Anesthesia & Monitoring Guidelines for Dogs & Cats (2020)",
        href: "https://www.aaha.org/resources/2020-aaha-anesthesia-and-monitoring-guidelines-for-dogs-and-cats/",
        source: "AAHA",
      },
      {
        label: "ANZCVS Veterinary Anaesthesia & Analgesia Chapter",
        href: "https://www.anzcvs.org.au/chapters/veterinary+anaesthesia+and+analgesia+chapter",
        source: "ANZCVS · Australia / NZ",
      },
    ],
  },

  dosing: {
    blurb:
      "Sharpen your drug-calculation and dosing maths with these veterinary-nursing focused resources.",
    links: [
      {
        label: "Veterinary Nursing Mathematics — Medication Dosages",
        href: "https://libguides.melbournepolytechnic.edu.au/VeterinaryNursingMathematics/Dosages",
        source: "Melbourne Polytechnic · Australia",
      },
      {
        label: "Top 5 drug calculations every vet nurse needs to know",
        href: "https://www.veterinaryinternalmedicinenursing.com/blog/top-5-calculations",
        source: "Veterinary Internal Medicine Nursing",
      },
      {
        label: "AAHA premedication & sedation drug combinations (dosing reference)",
        href: "https://www.aaha.org/resources/2020-aaha-anesthesia-and-monitoring-guidelines-for-dogs-and-cats/drug-combinations/premedication-and-sedation/",
        source: "AAHA",
      },
      {
        label: "Veterinary Nurses Council of Australia (VNCA)",
        href: "https://vnca.asn.au",
        source: "VNCA · Australia",
      },
    ],
  },

  admission: {
    blurb:
      "Sharpen your patient-admission, consent, fasting and biosecurity knowledge with these veterinary-nursing resources.",
    links: [
      {
        label: "Admitting and discharging patients — the veterinary nurse's role",
        href: "https://www.theveterinarynurse.com/clinical/admitting-and-discharging-patients",
        source: "The Veterinary Nurse",
      },
      {
        label: "WSAVA Global Anaesthesia Guidelines (pre-anaesthetic fasting)",
        href: "https://wsava.org/global-guidelines/global-anesthesia-guidelines/",
        source: "WSAVA",
      },
      {
        label: "Informed consent in veterinary practice",
        href: "https://www.avma.org/resources-tools/animal-health-and-welfare/informed-consent",
        source: "AVMA",
      },
      {
        label: "Veterinary Nurses Council of Australia (VNCA)",
        href: "https://vnca.asn.au",
        source: "VNCA · Australia",
      },
    ],
  },

  clinpath: {
    blurb:
      "Build your sample-handling and haematology/biochemistry interpretation skills with these laboratory and veterinary-nursing resources.",
    links: [
      {
        label: "Blood collection tubes & order of draw — sample handling",
        href: "https://www.theveterinarynurse.com/clinical/blood-sampling-in-practice",
        source: "The Veterinary Nurse",
      },
      {
        label: "Interpreting the complete blood count (CBC) in dogs & cats",
        href: "https://www.merckvetmanual.com/clinical-pathology-and-procedures/diagnostic-procedures-for-the-private-practice-laboratory/hematology",
        source: "MSD / Merck Veterinary Manual",
      },
      {
        label: "Interpreting serum biochemistry profiles",
        href: "https://www.merckvetmanual.com/clinical-pathology-and-procedures/diagnostic-procedures-for-the-private-practice-laboratory/serum-biochemical-analysis",
        source: "MSD / Merck Veterinary Manual",
      },
      {
        label: "Veterinary Nurses Council of Australia (VNCA)",
        href: "https://vnca.asn.au",
        source: "VNCA · Australia",
      },
    ],
  },

  urinalysis: {
    blurb:
      "Sharpen your urinalysis technique — appearance, specific gravity, dipstick and sediment — with these references.",
    links: [
      {
        label: "Complete urinalysis: collection, dipstick & sediment",
        href: "https://www.merckvetmanual.com/clinical-pathology-and-procedures/urinary-system/the-urinalysis-in-animals",
        source: "MSD / Merck Veterinary Manual",
      },
      {
        label: "Urine specific gravity & the refractometer",
        href: "https://www.theveterinarynurse.com/clinical/urinalysis-in-practice",
        source: "The Veterinary Nurse",
      },
      {
        label: "Interpreting urine dipstick results in dogs & cats",
        href: "https://todaysveterinarynurse.com/internal-medicine/urinalysis-a-complete-evaluation/",
        source: "Today's Veterinary Nurse",
      },
      {
        label: "Veterinary Nurses Council of Australia (VNCA)",
        href: "https://vnca.asn.au",
        source: "VNCA · Australia",
      },
    ],
  },

  wardrounds: {
    blurb:
      "Sharpen your inpatient medication-round skills — reading the treatment sheet, the medication rights, dose calculation and safe administration — with these resources.",
    links: [
      {
        label: "The five (and more) rights of medication administration",
        href: "https://www.theveterinarynurse.com/clinical/medicines-management-in-veterinary-practice",
        source: "The Veterinary Nurse",
      },
      {
        label: "Veterinary Nursing Mathematics — Medication Dosages",
        href: "https://libguides.melbournepolytechnic.edu.au/VeterinaryNursingMathematics/Dosages",
        source: "Melbourne Polytechnic · Australia",
      },
      {
        label: "Hospitalised patient care & treatment sheets",
        href: "https://todaysveterinarynurse.com/nursing-wellness/the-nursing-care-of-the-hospitalized-patient/",
        source: "Today's Veterinary Nurse",
      },
      {
        label: "Veterinary Nurses Council of Australia (VNCA)",
        href: "https://vnca.asn.au",
        source: "VNCA · Australia",
      },
    ],
  },

  triage: {
    blurb:
      "Strengthen your emergency phone-triage and patient-prioritisation skills with these reputable references.",
    links: [
      {
        label: "Initial Triage & Resuscitation of Small Animal Emergency Patients",
        href: "https://www.merckvetmanual.com/emergency-medicine-and-critical-care/evaluation-and-initial-treatment-of-small-animal-emergency-patients/initial-triage-and-resuscitation-of-small-animal-emergency-patients",
        source: "MSD / Merck Veterinary Manual",
      },
      {
        label: "How to triage the veterinary patient",
        href: "https://vetgirlontherun.com/how-to-triage-the-veterinary-patient-vetgirl-veterinary-continuing-education-blog/",
        source: "VetGirl CE",
      },
      {
        label: "Preparing your team for patient emergencies — effective triage",
        href: "https://www.dvm360.com/view/preparing-your-team-for-patient-emergencies-how-it-takes-a-team-to-triage-effectively-and-efficiently",
        source: "dvm360",
      },
      {
        label: "Veterinary Nurses Council of Australia (VNCA)",
        href: "https://vnca.asn.au",
        source: "VNCA · Australia",
      },
    ],
  },

  dental: {
    blurb:
      "Take your dental charting and COHAT knowledge further with these guideline and specialist-college resources.",
    links: [
      {
        label: "AAHA Dental Care Guidelines for Dogs & Cats (2019)",
        href: "https://www.aaha.org/resources/2019-aaha-dental-care-guidelines-for-dogs-and-cats/",
        source: "AAHA",
      },
      {
        label: "WSAVA Global Dental Guidelines",
        href: "https://wsava.org/global-guidelines/dental-guidelines/",
        source: "WSAVA",
      },
      {
        label: "American Veterinary Dental College (AVDC) — nomenclature & resources",
        href: "https://avdc.org",
        source: "AVDC",
      },
      {
        label: "Veterinary Nurses Council of Australia (VNCA)",
        href: "https://vnca.asn.au",
        source: "VNCA · Australia",
      },
    ],
  },

  nutrition: {
    blurb:
      "Reinforce your nutritional assessment, energy-calculation and therapeutic-feeding knowledge with these authoritative resources.",
    links: [
      {
        label: "WSAVA Global Nutrition Guidelines & Toolkit (incl. calorie & BCS tools)",
        href: "https://wsava.org/global-guidelines/global-nutrition-guidelines/",
        source: "WSAVA",
      },
      {
        label: "AAHA Nutritional Assessment Guidelines for Dogs & Cats",
        href: "https://www.aaha.org/resources/2010-aaha-nutritional-assessment-guidelines/",
        source: "AAHA",
      },
      {
        label: "Pet Nutrition Alliance — calculators & nutrition tools",
        href: "https://petnutritionalliance.org/",
        source: "Pet Nutrition Alliance",
      },
      {
        label: "Veterinary Nurses Council of Australia (VNCA)",
        href: "https://vnca.asn.au",
        source: "VNCA · Australia",
      },
    ],
  },

  conflict: {
    blurb:
      "Build your skills in difficult conversations, de-escalation and complaint handling with these communication resources.",
    links: [
      { label: "Communication & conflict in veterinary practice", href: "https://www.theveterinarynurse.com/", source: "The Veterinary Nurse" },
      { label: "AVA — wellbeing & difficult client interactions", href: "https://www.ava.com.au/member-services/thrive/", source: "Australian Veterinary Association" },
      { label: "Handling client complaints & de-escalation", href: "https://todaysveterinarynurse.com/", source: "Today's Veterinary Nurse" },
      { label: "Veterinary Nurses Council of Australia (VNCA)", href: "https://vnca.asn.au", source: "VNCA · Australia" },
    ],
  },

  grief: {
    blurb:
      "Resources on euthanasia best practice, bereavement support and managing compassion fatigue.",
    links: [
      { label: "Companion animal euthanasia & client support", href: "https://www.ava.com.au/", source: "Australian Veterinary Association" },
      { label: "Pet loss & grief support resources", href: "https://todaysveterinarynurse.com/", source: "Today's Veterinary Nurse" },
      { label: "Compassion fatigue & veterinary team wellbeing", href: "https://www.ava.com.au/member-services/thrive/", source: "AVA THRIVE" },
      { label: "Veterinary Nurses Council of Australia (VNCA)", href: "https://vnca.asn.au", source: "VNCA · Australia" },
    ],
  },

  reception: {
    blurb:
      "Front-desk workflow, client service, privacy and practice-coordination references.",
    links: [
      { label: "Veterinary reception & client service skills", href: "https://www.theveterinarynurse.com/", source: "The Veterinary Nurse" },
      { label: "Privacy of client information — Australian Privacy Principles", href: "https://www.oaic.gov.au/privacy/australian-privacy-principles", source: "OAIC · Australia" },
      { label: "Veterinary Nurses Council of Australia (VNCA)", href: "https://vnca.asn.au", source: "VNCA · Australia" },
    ],
  },

  dispensing: {
    blurb:
      "Safe dispensing, medicines scheduling, labelling and owner counselling references.",
    links: [
      { label: "Medicines management & dispensing in practice", href: "https://www.theveterinarynurse.com/clinical/medicines-management-in-veterinary-practice", source: "The Veterinary Nurse" },
      { label: "Veterinary Nursing Mathematics — dosages", href: "https://libguides.melbournepolytechnic.edu.au/VeterinaryNursingMathematics/Dosages", source: "Melbourne Polytechnic · Australia" },
      { label: "Scheduling of medicines (Poisons Standard / SUSMP)", href: "https://www.tga.gov.au/products/scheduling/scheduling-basics", source: "TGA · Australia" },
      { label: "Veterinary Nurses Council of Australia (VNCA)", href: "https://vnca.asn.au", source: "VNCA · Australia" },
    ],
  },

  infection: {
    blurb:
      "Infection control, biosecurity, isolation and disinfection references for veterinary practice.",
    links: [
      { label: "Infection prevention & control in veterinary clinics", href: "https://www.ava.com.au/", source: "Australian Veterinary Association" },
      { label: "Canine parvovirus — disinfection & management", href: "https://wsava.org/global-guidelines/vaccination-guidelines/", source: "WSAVA" },
      { label: "Cleaning, disinfection & sterilisation principles", href: "https://todaysveterinarynurse.com/", source: "Today's Veterinary Nurse" },
      { label: "Veterinary Nurses Council of Australia (VNCA)", href: "https://vnca.asn.au", source: "VNCA · Australia" },
    ],
  },

  radiography: {
    blurb:
      "Radiographic positioning, exposure technique and radiation-safety (ALARA) references.",
    links: [
      { label: "Veterinary radiography positioning & technique", href: "https://www.theveterinarynurse.com/", source: "The Veterinary Nurse" },
      { label: "Radiation safety & ALARA in veterinary practice", href: "https://www.arpansa.gov.au/", source: "ARPANSA · Australia" },
      { label: "Veterinary Nurses Council of Australia (VNCA)", href: "https://vnca.asn.au", source: "VNCA · Australia" },
    ],
  },

  asepsis: {
    blurb:
      "Surgical asepsis, the sterile field, instrument handling and sterilisation references.",
    links: [
      { label: "Aseptic technique & the sterile field", href: "https://todaysveterinarynurse.com/", source: "Today's Veterinary Nurse" },
      { label: "Sterilisation & autoclave monitoring in practice", href: "https://www.theveterinarynurse.com/", source: "The Veterinary Nurse" },
      { label: "Veterinary Nurses Council of Australia (VNCA)", href: "https://vnca.asn.au", source: "VNCA · Australia" },
    ],
  },

  emergency: {
    blurb:
      "Emergency recognition, CPR (RECOVER) and first-aid references for the veterinary team.",
    links: [
      { label: "RECOVER CPR clinical guidelines", href: "https://recoverinitiative.org/cpr-guidelines/", source: "RECOVER Initiative" },
      { label: "Emergency & critical care nursing", href: "https://todaysveterinarynurse.com/", source: "Today's Veterinary Nurse" },
      { label: "Veterinary Nurses Council of Australia (VNCA)", href: "https://vnca.asn.au", source: "VNCA · Australia" },
    ],
  },
};
